import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { TRY, fmtDate, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePicker } from "@/components/DatePicker";
import { Banknote, CreditCard, ArrowLeftRight, ScrollText, Wallet, ReceiptText, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

export const PAY_METHODS = [
  { value: "nakit", label: "Nakit", Icon: Banknote },
  { value: "kredi_karti", label: "Kredi Kartı", Icon: CreditCard },
  { value: "havale", label: "Havale/EFT", Icon: ArrowLeftRight },
  { value: "cek", label: "Çek", Icon: ScrollText },
];
export const methodLabelOf = (m) => (PAY_METHODS.find((x) => x.value === m) || {}).label || m || "";
export const invoicePaid = (inv) => (inv?.payment_history || []).reduce((s, h) => s + Number(h.amount || 0), 0);
export const invoiceRemaining = (inv) => Math.max(0, Number(inv?.total || 0) - invoicePaid(inv));
const invNo = (inv) => inv?.invoice_no || inv?.qnb?.invoice_no || "";
const dirOfInvoice = (inv) => (inv?.type === "alis" ? "giden" : "gelen");

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}><Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
const Step = ({ n, title, hint }) => (
  <div className="flex items-center gap-2">
    <span className="h-5 w-5 rounded-full bg-primary text-white text-[11px] font-bold flex items-center justify-center shrink-0">{n}</span>
    <span className="text-sm font-bold">{title}</span>
    {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
  </div>
);

const blankForm = () => ({ amount: "", method: "", date: todayISO(), note: "", noteAuto: true, due_date: "", from_whom: "", serial_no: "", document_no: "" });
const PAY_NOTE_HEAD = { nakit: "Nakit", kredi_karti: "Kredi kartı", havale: "Havale/EFT", cek: "Çek" };
// Yeni ödemede yönteme göre otomatik açıklama (sunucudaki payment_note ile aynı metin); kullanıcı yazarsa onunki kalır
export const payNoteFor = (method, dir, f = {}) => {
  const head = PAY_NOTE_HEAD[method];
  if (!head) return "";
  let s = `${head} ile ödeme ${dir === "giden" ? "yapılmıştır" : "alınmıştır"}.`;
  if (method === "cek") {
    const t = (v) => String(v || "").trim();
    const parts = [t(f.serial_no) && `Seri No: ${t(f.serial_no)}`, f.due_date && `Vade: ${fmtDate(f.due_date)}`,
      t(f.from_whom) && `${dir === "giden" ? "Kime" : "Kimden"}: ${t(f.from_whom)}`, t(f.document_no) && `Belge No: ${t(f.document_no)}`].filter(Boolean);
    if (parts.length) s += ` ${parts.join(" · ")}`;
  }
  return s;
};

/** Ortak Ödeme / Tahsilat diyaloğu — HER ZAMAN sorar: 1) Hedef (Cari Hesap / Fatura) 2) Tutar 3) Yöntem.
 *  invoice verilirse hedef fatura önseçili gelir ve KALAN tutar önerilir (kullanıcı onaylar, otomatik kapatmaz).
 *  edit verilirse mevcut cari ödemesi düzenlenir. */
export default function PaymentDialog({ open, onClose, contactTitle, contact, invoices, invoice, edit, direction, onSaved }) {
  const [target, setTarget] = useState("cari");
  const [invId, setInvId] = useState("");
  const [fetched, setFetched] = useState([]);
  const [cur, setCur] = useState(null);
  const [dir, setDir] = useState("gelen");
  const [form, setForm] = useState(blankForm());
  const [autoAmt, setAutoAmt] = useState(false);
  const [saving, setSaving] = useState(false);
  const title = contactTitle || invoice?.contact_title || edit?.contact_title || "";

  useEffect(() => {
    if (!open) return;
    const f = blankForm();
    if (edit) {
      setTarget("cari"); setInvId(""); setDir(edit.direction || "gelen");
      setForm({ ...f, amount: String(edit.amount ?? ""), method: edit.method || "", date: edit.date || f.date, note: edit.note || "", noteAuto: false, due_date: edit.due_date || "", from_whom: edit.from_whom || "", serial_no: edit.serial_no || "", document_no: edit.document_no || "" });
      setAutoAmt(false); setCur(null);
      return;
    }
    if (invoice) {
      const rem = invoiceRemaining(invoice);
      setTarget("fatura"); setInvId(invoice.id); setCur(invoice); setDir(dirOfInvoice(invoice));
      setForm({ ...f, amount: rem > 0 ? rem.toFixed(2) : "" }); setAutoAmt(rem > 0);
    } else {
      setTarget("cari"); setInvId(""); setCur(null);
      setDir(direction || "gelen");   // varsayılan her yerde MÜŞTERİDEN TAHSİLAT (kullanıcı kararı)
      setForm(f); setAutoAmt(false);
    }
    if (!invoices && title) api.get(`/invoices?contact=${encodeURIComponent(title)}`).then((r) => setFetched(r.data || [])).catch(() => setFetched([]));
  }, [open, invoice, edit]); // eslint-disable-line react-hooks/exhaustive-deps

  // Açıklama elle yazılmadıkça yöntem / yön / çek bilgilerine göre otomatik güncellenir (yalnız yeni kayıt)
  useEffect(() => {
    if (!open || edit) return;
    setForm((x) => (x.noteAuto ? { ...x, note: payNoteFor(x.method, dir, x.method === "cek" ? x : {}) } : x));
  }, [open, edit, dir, form.method, form.due_date, form.serial_no, form.from_whom, form.document_no, form.noteAuto]);

  const list = useMemo(() => {
    const src = (invoices || fetched || []).filter((i) => !i.is_draft);
    const merged = invoice && !src.some((i) => i.id === invoice.id) ? [invoice, ...src] : src;
    return [...merged].sort((a, b) => (invoiceRemaining(b) > 0) - (invoiceRemaining(a) > 0) || String(b.date || "").localeCompare(String(a.date || "")));
  }, [invoices, fetched, invoice]);

  const selInv = target === "fatura" ? (cur && cur.id === invId ? cur : list.find((i) => i.id === invId)) : null;
  const remain = selInv ? invoiceRemaining(selInv) : 0;
  const amt = Number(form.amount) || 0;
  const over = selInv && amt > remain + 0.005;
  const canSave = amt > 0 && !!form.method && (target === "cari" || !!selInv) && !over && (form.method !== "cek" || !!form.due_date);

  const pickInvoice = (id) => {
    const inv = list.find((i) => i.id === id);
    setInvId(id); setCur(inv || null);
    if (inv) {
      setDir(dirOfInvoice(inv));
      const rem = invoiceRemaining(inv);
      if (autoAmt || !form.amount) { setForm((x) => ({ ...x, amount: rem > 0 ? rem.toFixed(2) : "" })); setAutoAmt(rem > 0); }
    }
  };
  const setAmount = (v, auto = false) => { setForm((x) => ({ ...x, amount: v })); setAutoAmt(auto); };

  const save = async () => {
    if (!(amt > 0)) return toast.error("Tutar girin");
    if (!form.method) return toast.error("Ödeme yöntemi seçin");
    if (target === "fatura" && !selInv) return toast.error("Fatura seçin");
    if (over) return toast.error(`Tutar faturanın kalan bakiyesini (${TRY(remain)}) aşamaz`);
    if (form.method === "cek" && !form.due_date) return toast.error("Çek vadesi seçin");
    setSaving(true);
    try {
      let result;
      const isCek = form.method === "cek";   // çek bilgileri yalnız çekli ödemede kaydedilir
      const common = { amount: amt, method: form.method, date: form.date, note: form.note, due_date: isCek ? form.due_date : "", from_whom: isCek ? form.from_whom : "", serial_no: isCek ? form.serial_no : "", document_no: isCek ? form.document_no : "" };
      if (edit) {
        result = (await api.put(`/payments/${edit.id}`, { ...edit, ...common, direction: dir })).data;
        toast.success("Ödeme güncellendi");
      } else if (target === "fatura") {
        result = (await api.post(`/invoices/${selInv.id}/payments`, common)).data;
        const left = invoiceRemaining(result);
        toast.success(left <= 0.005 ? `Fatura #${invNo(selInv) || "—"} tamamen ${dir === "gelen" ? "tahsil edildi" : "ödendi"}` : `${TRY(amt)} işlendi · kalan ${TRY(left)}`);
      } else {
        result = (await api.post("/payments", { ...common, contact_title: title, direction: dir, invoice_id: "", invoice_no: "", cleared: false })).data;
        toast.success(`${TRY(amt)} cari hesaba ${dir === "gelen" ? "tahsilat" : "ödeme"} olarak işlendi`);
      }
      onSaved && onSaved(result, { target: edit ? "edit" : target });
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const delHist = async (pid) => {
    if (!selInv || !await confirm("Bu ödeme kaydı silinsin mi?")) return;
    try {
      const { data } = await api.delete(`/invoices/${selInv.id}/payments/${pid}`);
      setCur(data); onSaved && onSaved(data, { target: "delete" });
      const rem = invoiceRemaining(data);
      if (autoAmt) setForm((x) => ({ ...x, amount: rem > 0 ? rem.toFixed(2) : "" }));
    } catch (e) { toast.error(apiError(e)); }
  };

  const dirLabel = dir === "gelen" ? "Tahsilat" : "Ödeme";
  const TargetCard = ({ value, Icon, label, desc, disabled }) => (
    <button type="button" disabled={disabled} onClick={() => { setTarget(value); if (value === "cari") { setInvId(""); setCur(null); if (autoAmt) setAmount(""); } }}
      className={`text-left rounded-xl border-2 p-3 transition-colors ${target === value ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`} data-testid={`pay-target-${value}`}>
      <div className="flex items-center gap-2 font-bold text-sm"><Icon className={`h-4 w-4 ${target === value ? "text-primary" : "text-muted-foreground"}`} />{label}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">{desc}</div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[96vw] sm:max-w-3xl max-h-[94vh] overflow-y-auto" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">{edit ? "Ödemeyi Düzenle" : "Ödeme / Tahsilat Kaydı"}</DialogTitle>
          <p className="text-sm text-muted-foreground -mt-1 truncate">{title || "—"}</p>
        </DialogHeader>

        <div className={`grid gap-4 ${edit ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
        {!edit && (
          <div className="space-y-2">
            <Step n={1} title="Nereye işlensin?" />
            <div className="grid grid-cols-2 gap-2">
              <TargetCard value="cari" Icon={Wallet} label="Cari Hesap" desc="Genel bakiyeye mahsuben (fatura bağı yok)" />
              <TargetCard value="fatura" Icon={ReceiptText} label="Belirli Fatura" desc={list.length ? "Seçilen faturanın kalanından düşer" : "Bu cariye ait fatura yok"} disabled={!list.length} />
            </div>
            {target === "fatura" && (
              <div className="space-y-2">
                <Select value={invId || "none"} onValueChange={(v) => v !== "none" && pickInvoice(v)}>
                  <SelectTrigger data-testid="pay-invoice"><SelectValue placeholder="Fatura seçin" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>— Fatura seçin —</SelectItem>
                    {list.map((inv) => { const r = invoiceRemaining(inv); return (
                      <SelectItem key={inv.id} value={inv.id} data-testid={`pay-invoice-${inv.id}`}>
                        {inv.type === "alis" ? "Alış" : "Satış"} #{invNo(inv) || "—"} · {fmtDate(inv.date)} · {TRY(inv.total)} · {r > 0.005 ? `kalan ${TRY(r)}` : "kapalı"}
                      </SelectItem>
                    ); })}
                  </SelectContent>
                </Select>
                {selInv && (
                  <div className="rounded-lg border bg-muted/30 p-2 space-y-2" data-testid="pay-invoice-summary">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div><div className="text-[10px] uppercase font-bold text-muted-foreground">Toplam</div><div className="font-head font-black tabular-nums text-sm">{TRY(selInv.total)}</div></div>
                      <div><div className="text-[10px] uppercase font-bold text-emerald-600">{dir === "gelen" ? "Tahsil edilen" : "Ödenen"}</div><div className="font-head font-black tabular-nums text-sm text-emerald-700">{TRY(invoicePaid(selInv))}</div></div>
                      <div><div className="text-[10px] uppercase font-bold text-amber-600">Kalan</div><div className="font-head font-black tabular-nums text-sm text-amber-700" data-testid="pay-remaining">{TRY(remain)}</div></div>
                    </div>
                    {(selInv.payment_history || []).length > 0 && (
                      <div className="border-t pt-1.5 space-y-1 max-h-24 overflow-y-auto">
                        {(selInv.payment_history || []).map((h) => (
                          <div key={h.id} className="flex items-center gap-2 text-xs" data-testid={`pay-hist-${h.id}`}>
                            <span className="text-muted-foreground">{fmtDate(h.date)} · {methodLabelOf(h.method)}{h.note ? ` · ${h.note}` : ""}</span>
                            <span className="ml-auto tabular-nums font-semibold">{TRY(h.amount)}</span>
                            <button type="button" className="text-destructive hover:opacity-70" onClick={() => delHist(h.id)} data-testid={`del-pay-${h.id}`} title="Kaydı sil"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {target === "cari" && (
              <div className="rounded-lg border border-dashed bg-muted/20 p-3 text-xs text-muted-foreground" data-testid="pay-cari-hint">
                Tutar doğrudan cari bakiyeye işlenir; ekstre ve Cari Defter'de "Ödemeler" bölümünde görünür.
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
        <div className="space-y-2">
          <Step n={edit ? 1 : 2} title="Tutar" hint={selInv && autoAmt ? "kalan bakiye önerildi — kontrol edip onaylayın" : ""} />
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Input type="number" step="any" inputMode="decimal" className="h-11 text-lg font-bold tabular-nums pr-8" placeholder="0,00" value={form.amount} onChange={(e) => setAmount(e.target.value)} data-testid="pay-amount" autoFocus />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">₺</span>
            </div>
            {selInv && remain > 0 && (
              <>
                <Button type="button" size="sm" variant="outline" onClick={() => setAmount(remain.toFixed(2), true)} data-testid="pay-amount-full">Kalanın Tümü</Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setAmount((remain / 2).toFixed(2))} data-testid="pay-amount-half">Yarısı</Button>
              </>
            )}
          </div>
          {over && <p className="text-xs text-destructive flex items-center gap-1" data-testid="pay-over-warning"><AlertTriangle className="h-3.5 w-3.5" />Tutar kalan bakiyeyi ({TRY(remain)}) aşıyor.</p>}
          {selInv && amt > 0 && !over && <p className="text-xs text-muted-foreground" data-testid="pay-after-hint">{remain - amt <= 0.005 ? <span className="text-emerald-700 font-semibold inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Bu tutarla fatura tamamen kapanır.</span> : <>Kayıttan sonra kalan: <b className="tabular-nums">{TRY(remain - amt)}</b> (kısmi {dirLabel.toLowerCase()})</>}</p>}
        </div>

        <div className="space-y-2">
          <Step n={edit ? 2 : 3} title="Ödeme Yöntemi" />
          <div className="grid grid-cols-4 gap-2" data-testid="pay-methods">
            {PAY_METHODS.map(({ value, label, Icon }) => (
              <button key={value} type="button" onClick={() => setForm((x) => ({ ...x, method: value }))}
                className={`rounded-lg border-2 px-2 py-1.5 text-xs font-semibold flex flex-col items-center gap-1 transition-colors ${form.method === value ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40 text-muted-foreground"}`} data-testid={`pay-method-${value}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          {form.method === "cek" && (
            <div className="grid grid-cols-2 gap-2 rounded-lg border border-amber-200 bg-amber-50/60 p-2">
              <div className="col-span-2 text-[10px] font-bold uppercase tracking-wide text-amber-700">Çek Bilgileri (vadesi gelmemiş evraklara eklenir)</div>
              <F label="Çek Vadesi *"><DatePicker value={form.due_date || ""} onChange={(v) => setForm((x) => ({ ...x, due_date: v }))} placeholder="Vade seçin" data-testid="pay-due-date" /></F>
              <F label={dir === "gelen" ? "Kimden Alındı" : "Kime Verildi"}><Input value={form.from_whom} onChange={(e) => setForm((x) => ({ ...x, from_whom: e.target.value }))} placeholder="Ör. Ahmet Yılmaz" data-testid="pay-from-whom" /></F>
              <F label="Çek Seri No"><Input value={form.serial_no} onChange={(e) => setForm((x) => ({ ...x, serial_no: e.target.value }))} data-testid="pay-serial-no" /></F>
              <F label="Belge / Evrak No"><Input value={form.document_no} onChange={(e) => setForm((x) => ({ ...x, document_no: e.target.value }))} data-testid="pay-document-no" /></F>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <F label="Tarih"><DatePicker value={form.date} onChange={(v) => setForm((x) => ({ ...x, date: v }))} data-testid="pay-date" /></F>
          {target === "cari" ? (
            <F label="Yön">
              <Select value={dir} onValueChange={setDir}>
                <SelectTrigger data-testid="pay-dir"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gelen">Tahsilat (müşteriden)</SelectItem>
                  <SelectItem value="giden">Ödeme (tedarikçiye)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          ) : (
            <F label="Yön"><div className="h-10 rounded-md border px-3 flex items-center text-sm font-medium" data-testid="pay-dir-fixed">{dirLabel} · faturadan</div></F>
          )}
          <F className="col-span-2" label={edit ? "Açıklama" : "Açıklama (yönteme göre otomatik yazılır, değiştirebilirsiniz)"}><Input value={form.note} onChange={(e) => setForm((x) => ({ ...x, note: e.target.value, noteAuto: !edit && !e.target.value.trim() }))} placeholder="Ödeme açıklaması" data-testid="pay-note" /></F>
        </div>
        </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-3" data-testid="payment-footer">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={!canSave || saving} data-testid="save-payment">
            {saving ? "Kaydediliyor…" : amt > 0 ? `${TRY(amt)} · ${form.method ? methodLabelOf(form.method) : "yöntem seçin"} → ${target === "fatura" ? `Fatura #${invNo(selInv) || "—"}` : "Cari Hesap"}` : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Toplu tahsilat/ödeme — seçilen her fatura için kalan bakiye ÖNERİLİR; tutar düzenlenebilir; ortak yöntem + tarih. */
export function BulkPaymentDialog({ open, onClose, invoices = [], isBuy = false, onDone }) {
  const [rows, setRows] = useState([]);
  const [method, setMethod] = useState("");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setRows(invoices.filter((i) => invoiceRemaining(i) > 0.005).map((i) => ({ inv: i, amount: invoiceRemaining(i).toFixed(2) })));
    setMethod(""); setDate(todayISO()); setNote("");
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const invalid = rows.some((r) => (Number(r.amount) || 0) > invoiceRemaining(r.inv) + 0.005);
  const active = rows.filter((r) => (Number(r.amount) || 0) > 0).length;
  const word = isBuy ? "Ödeme" : "Tahsilat";

  const save = async () => {
    if (!method) return toast.error("Ödeme yöntemi seçin");
    if (!active) return toast.error("En az bir faturaya tutar girin");
    if (invalid) return toast.error("Bir faturada tutar kalan bakiyeyi aşıyor");
    setSaving(true);
    let ok = 0;
    for (const r of rows) {
      const a = Number(r.amount) || 0;
      if (!(a > 0)) continue;
      try { await api.post(`/invoices/${r.inv.id}/payments`, { amount: a, method, date, note }); ok += 1; }
      catch (e) { toast.error(`#${invNo(r.inv) || "—"}: ${apiError(e)}`); }
    }
    setSaving(false);
    if (ok) toast.success(`${ok} faturaya ${word.toLowerCase()} işlendi · ${TRY(total)}`);
    onDone && onDone(); onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="bulk-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-2xl">Toplu {word}</DialogTitle>
          <p className="text-sm text-muted-foreground -mt-1">Her fatura için <b>kalan bakiye önerildi</b>; tutarları kontrol edip düzenleyebilirsiniz. Hiçbir fatura onayınız olmadan kapatılmaz.</p>
        </DialogHeader>
        {rows.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center" data-testid="bulk-empty">Seçilen faturaların hepsi zaten kapalı.</p> : (
          <div className="rounded-lg border divide-y" data-testid="bulk-rows">
            {rows.map((r, i) => { const rem = invoiceRemaining(r.inv); const a = Number(r.amount) || 0; return (
              <div key={r.inv.id} className="flex items-center gap-3 p-2.5" data-testid={`bulk-row-${r.inv.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold truncate">{r.inv.contact_title}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">#{invNo(r.inv) || "—"} · {fmtDate(r.inv.date)} · Toplam {TRY(r.inv.total)} · Kalan <b className="text-amber-700">{TRY(rem)}</b></div>
                </div>
                <div className="flex items-center gap-1">
                  <Input type="number" step="any" inputMode="decimal" className={`h-9 w-32 text-right tabular-nums font-semibold ${a > rem + 0.005 ? "border-destructive" : ""}`} value={r.amount} onChange={(e) => setRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, amount: e.target.value } : x)))} data-testid={`bulk-amount-${r.inv.id}`} />
                  <Button type="button" size="sm" variant="ghost" className="h-9 px-2 text-xs" onClick={() => setRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, amount: rem.toFixed(2) } : x)))} title="Kalanın tümü">Tümü</Button>
                  <Button type="button" size="sm" variant="ghost" className="h-9 px-2 text-xs" onClick={() => setRows((rs) => rs.map((x, idx) => (idx === i ? { ...x, amount: "" } : x)))} title="Bu faturayı atla">Atla</Button>
                </div>
              </div>
            ); })}
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Ödeme Yöntemi (tümü için)</Label>
          <div className="grid grid-cols-4 gap-2">
            {PAY_METHODS.filter((m) => m.value !== "cek").map(({ value, label, Icon }) => (
              <button key={value} type="button" onClick={() => setMethod(value)} className={`rounded-lg border-2 px-2 py-2 text-xs font-semibold flex flex-col items-center gap-1 ${method === value ? "border-primary bg-primary/5 text-primary" : "border-border hover:border-primary/40 text-muted-foreground"}`} data-testid={`bulk-method-${value}`}><Icon className="h-4 w-4" />{label}</button>
            ))}
          </div>
          <p className="text-[11px] text-muted-foreground">Çek ile ödeme için faturayı tek tek işleyin (vade/seri bilgisi gerekir).</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Tarih"><DatePicker value={date} onChange={setDate} data-testid="bulk-date" /></F>
          <F label="Açıklama"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Boş bırakılırsa yönteme göre otomatik yazılır" data-testid="bulk-note" /></F>
        </div>
        <DialogFooter className="sticky -bottom-6 -mx-6 -mb-6 px-6 py-3 bg-background border-t z-10" data-testid="bulk-payment-footer">
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={save} disabled={saving || !rows.length || invalid || !active || !method} data-testid="bulk-save">{saving ? "İşleniyor…" : `${active} fatura · ${TRY(total)} işle`}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
